import React, { useEffect } from "react";
import {
    DollarSign,
    Wallet,
    Clock,
    ArrowRight,
    Shield,
    Gift,
    Timer,
    AlertCircle,
    CheckCircle2,
    ArrowUpCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const HowItWorksScreen = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);
    const steps = [
        {
            color: "bg-blue-500",
            description: t("steps.connectClaim.description"),
            icon: Wallet,
            title: t("steps.connectClaim.title"),
        },
        {
            color: "bg-green-500",
            description: t("steps.exploreAuctions.description"),
            icon: DollarSign,
            title: t("steps.exploreAuctions.title"),
        },
        {
            color: "bg-amber-500",
            description: t("steps.bidEarn.description"),
            icon: ArrowUpCircle,
            title: t("steps.bidEarn.title"),
        },
        {
            color: "bg-purple-500",
            description: t("steps.fomoMode.description"),
            icon: Timer,
            title: t("steps.fomoMode.title"),
        },
    ];

    const benefits = [
        {
            icon: Shield,
            title: t("benefits.transparentProcess.title"),
            description: t("benefits.transparentProcess.description"),
        },
        {
            icon: Gift,
            title: t("benefits.tokenRewards.title"),
            description: t("benefits.tokenRewards.description"),
        },
        {
            icon: DollarSign,
            title: t("benefits.competitivePricing.title"),
            description: t("benefits.competitivePricing.description"),
        },
        {
            icon: CheckCircle2,
            title: t("benefits.secureTrustless.title"),
            description: t("benefits.secureTrustless.description"),
        },
    ];

    return (
        <div className="min-h-screen  text-zinc-100">
            <main className="container mx-auto px-4 py-8 pt-24">
                <h1 className="text-4xl font-bold mb-6 text-center bg-gradient-to-r from-amber-500 to-amber-300 bg-clip-text text-transparent">
                    {t("main.title")}
                </h1>

                <div className="bg-zinc-800/50 rounded-2xl p-6 mb-12 text-center backdrop-blur-sm border border-zinc-700/50">
                    <h2 className="text-2xl font-bold mb-2">{t("main.subtitle")}</h2>
                    <p className="text-lg text-zinc-400">
                        {t("main.description")}{" "}
                        <span className="font-bold text-amber-500">{t("main.bidTokens")}</span>
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                    {steps.map((step, index) => (
                        <div
                            key={index}
                            className="bg-zinc-800/50 rounded-xl p-6 backdrop-blur-sm border border-zinc-700/50 hover:border-amber-500/50 transition-all duration-300"
                        >
                            <div className="bg-amber-500 rounded-full p-3 inline-block mb-4">
                                <step.icon size={24} className="text-zinc-900" />
                            </div>
                            <h3 className="text-xl font-bold mb-2 text-zinc-100">{step.title}</h3>
                            <p className="text-zinc-400">{step.description}</p>
                        </div>
                    ))}
                </div>

                <div className="bg-zinc-800/50 rounded-xl p-8 backdrop-blur-sm border border-zinc-700/50 mb-12">
                    <h3 className="text-2xl font-bold mb-6 text-zinc-100">{t("rules.title")}</h3>
                    <div className="space-y-4">
                        <div className="flex items-start space-x-4">
                            <DollarSign className="text-amber-500 mt-1 flex-shrink-0" />
                            <div>
                                <h4 className="font-semibold">{t("rules.bidCosts.title")}</h4>
                                <p className="text-zinc-400">{t("rules.bidCosts.description")}</p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-4">
                            <Clock className="text-amber-500 mt-1 flex-shrink-0" />
                            <div>
                                <h4 className="font-semibold">{t("rules.fomoModeTimer.title")}</h4>
                                <p className="text-zinc-400">
                                    {t("rules.fomoModeTimer.description")}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-4">
                            <AlertCircle className="text-amber-500 mt-1 flex-shrink-0" />
                            <div>
                                <h4 className="font-semibold">{t("rules.refundPolicy.title")}</h4>
                                <p className="text-zinc-400">
                                    {t("rules.refundPolicy.description")}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                    {benefits.map((benefit, index) => (
                        <div
                            key={index}
                            className="bg-zinc-800/50 rounded-xl p-6 backdrop-blur-sm border border-zinc-700/50"
                        >
                            <benefit.icon size={24} className="text-amber-500 mb-4" />
                            <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                            <p className="text-zinc-400 text-sm">{benefit.description}</p>
                        </div>
                    ))}
                </div>

                <div className="text-center">
                    <button
                        onClick={() => navigate("/auctions")}
                        className="bg-amber-500 text-zinc-900 py-3 px-8 rounded-xl hover:bg-amber-600 transition-all duration-200 text-lg font-bold inline-flex items-center"
                    >
                        {t("button.startBidding")} <ArrowRight size={20} className="ml-2" />
                    </button>
                </div>
            </main>
        </div>
    );
};

export default HowItWorksScreen;
